import { useRef } from "react";
import { Image as ImageIcon, Upload, Trash2, Check, X } from "lucide-react";
import { Spinner, cn } from "@/ui";

// Chat wallpapers that COMPLEMENT the kid's chosen UI theme. Each option derives from the
// live accent hue (--accent-h) + theme surface vars (see themes.js), so the whole palette of
// background choices recolors whenever the UI color changes. Kept in sync with backgrounds.tsx.
const H = "var(--accent-h)";
export const PRESETS = [
  { id: "solid", label: "Solid", css: "hsl(var(--background))" },
  {
    id: "glow",
    label: "Glow",
    css:
      "radial-gradient(at 22% 12%, hsl(var(--primary) / 0.30) 0px, transparent 48%)," +
      "radial-gradient(at 88% 8%, hsl(var(--primary) / 0.14) 0px, transparent 42%)," +
      "hsl(var(--background))",
  },
  { id: "tint", label: "Tint", css: `linear-gradient(160deg, hsl(${H} 48% 16%) 0%, hsl(var(--deep)) 100%)` },
  { id: "cool", label: "Cool", css: `linear-gradient(160deg, hsl(calc(${H} - 42) 44% 16%) 0%, hsl(var(--deep)) 100%)` },
  { id: "warm", label: "Warm", css: `linear-gradient(160deg, hsl(calc(${H} + 42) 50% 16%) 0%, hsl(var(--deep)) 100%)` },
  { id: "dusk", label: "Dusk", css: `linear-gradient(155deg, hsl(calc(${H} + 26) 52% 17%) 0%, hsl(calc(${H} - 26) 44% 11%) 100%)` },
];

export function presetById(id) {
  return PRESETS.find((p) => p.id === id) || null;
}

// Absolute wallpaper layer. Render inside a `relative` container, behind the content.
export function ChatBackground({ bg, imageUrl }) {
  if (!bg || bg.type === "none") return null;
  if (bg.type === "image") {
    return (
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-background/30" />
      </div>
    );
  }
  const preset = presetById(bg.value);
  if (!preset) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0"
      style={{ background: preset.css }}
      aria-hidden
    />
  );
}

export function BackgroundPicker({ open, onClose, value, onPreset, onUpload, onClear, uploading }) {
  const fileRef = useRef(null);
  if (!open) return null;
  const isPreset = value?.type === "preset";
  const isImage = value?.type === "image";
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div
        data-testid="background-picker"
        className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Chat background</h2>
          <button data-testid="background-close" onClick={onClose} className="p-1"><X className="h-5 w-5" /></button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          data-testid="background-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
        />

        <div className="grid grid-cols-3 gap-2.5">
          {PRESETS.map((p) => {
            const selected = isPreset && value.value === p.id;
            return (
              <button
                key={p.id}
                data-testid={`bg-preset-${p.id}`}
                onClick={() => onPreset(p.id)}
                className={cn(
                  "relative h-20 overflow-hidden rounded-xl border transition-all",
                  selected ? "border-primary ring-2 ring-primary" : "border-border hover:opacity-90",
                )}
                style={{ background: p.css }}
                title={p.label}
              >
                {selected ? (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                ) : null}
                <span className="absolute bottom-1 left-2 text-[11px] font-semibold text-foreground/80">{p.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <button
            data-testid="bg-upload-photo"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className={cn(
              "flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors",
              isImage ? "border-primary text-primary" : "border-border hover:bg-accent",
            )}
          >
            {uploading ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
            {isImage ? "Change photo" : "Upload a photo"}
          </button>
          {isImage ? (
            <span className="flex items-center gap-1.5 self-center text-xs text-muted-foreground">
              <ImageIcon className="h-3.5 w-3.5" /> Using your photo
            </span>
          ) : null}
          {value && value.type !== "none" ? (
            <button
              data-testid="bg-remove"
              onClick={onClear}
              className="flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="h-4 w-4" /> Remove background
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
